/** The walk face's state on the Decide page: which face, where the walk stands, the draft it reads, and how a desk or Events is opened on either face. */
import { useMemo } from "react";
import type { ContentCatalog, DraftView } from "@bsim/contracts";
import type { DecisionFamily } from "@bsim/domain";
import type { TabId } from "../DeskTabs";
import { useFace } from "./useFace";
import { tabOfStep, useWalkPosition } from "./useWalkPosition";
import type { NextStep } from "../nextStep";
import { stepForNextStep, type WalkInput } from "./walkSteps";

export function useWalkFace(a: {
  /** The draft with its marks (useReviewMarks.draft). */ readonly marked: DraftView;
  readonly draft: DraftView; readonly missingRationales: readonly DecisionFamily[]; readonly stillChanged: ReadonlySet<string>;
  readonly catalog: ContentCatalog; readonly hasCards: boolean; readonly cardsOwed: boolean;
  readonly userId: string; readonly search: string; readonly reviewing: boolean; readonly tab: TabId; readonly setTab: (t: TabId) => void;
}) {
  const { face, setFace } = useFace(a.userId);
  const { marked, draft, missingRationales, stillChanged } = a;
  const walkDraft = useMemo<DraftView>(() => ({ ...marked, missingRationales: [...missingRationales], changedFamilies: draft.changedFamilies.filter((f) => stillChanged.has(f)) }), [marked, missingRationales, draft.changedFamilies, stillChanged]);
  const walkInput: WalkInput = { draft: walkDraft, catalog: a.catalog, hasCards: a.hasCards, cardsOwed: a.cardsOwed };
  const walk = useWalkPosition(walkInput, a.search);
  const walkStep = walk.current;
  /** The walk's region is shown (a finished walk shows its finished state there); `walkOn` is a step being shown. */
  const walkRegion = face === "walk" && !a.reviewing;
  const walkOn = walkRegion && walkStep !== null;
  const activeTab: TabId = walkOn ? tabOfStep(walkStep) : a.tab;
  /** Opens a desk or the Events: the sheet's tab, or the walk's matching step. */
  const toTab = (t: TabId): void => {
    if (!walkRegion) { a.setTab(t); return; }
    const to = t === "events" ? walk.steps.find((s) => s.kind === "cards") : walk.firstOf(t);
    if (to !== undefined && to !== null) walk.goTo(to);
  };
  /** A next-step button on the walk: the step it names. */
  const goForNext = (step: NextStep): void => { const to = stepForNextStep(walkInput, step); if (to !== null) walk.goTo(to); };
  /** Switching faces keeps the draft and the marks: the sheet opens on the walk's desk, the walk on the first decision not yet reviewed. */
  const switchFace = (f: typeof face): void => {
    if (f === face) return;
    if (f === "sheet") a.setTab(activeTab);
    else walk.resume();
    setFace(f);
  };
  return { face, walk, walkStep, walkDraft, walkInput, walkRegion, walkOn, activeTab, toTab, switchFace, goForNext };
}
